// Capa de acceso a datos de la tabla `banner`. Se administran desde el
// módulo Página principal (el bloque `banner_principal` referencia un
// subconjunto de estos vía `bloque_pagina.configuracion.ids_banner`), pero
// la tabla en sí es independiente del bloque — de ahí un módulo propio en
// vez de mezclarlo con bloques-pagina.js.

import { supabaseClient } from '@plataforma/supabase-client';

import { obtenerEstadoSesion } from '../estado-sesion.js';

import { eliminarArchivo, rutaDesdeUrlPublica, subirImagenBanner } from './almacenamiento.js';

function empresaIdActual() {
  return obtenerEstadoSesion().claims.empresa_id;
}

export async function listarBanners() {
  return supabaseClient()
    .from('banner')
    .select('id, titulo, imagen_url, imagen_movil_url, enlace_tipo, enlace_valor, orden, estado')
    .order('orden', { ascending: true });
}

/**
 * @param {File} archivo
 * @param {{ titulo: string, enlace_tipo?: string, enlace_valor?: string }} datos
 */
export async function crearBanner(archivo, datos) {
  const empresaId = empresaIdActual();
  const { url, error: errorSubida } = await subirImagenBanner(empresaId, archivo);
  if (errorSubida) return { data: null, error: errorSubida };

  return supabaseClient()
    .from('banner')
    .insert({ ...datos, empresa_id: empresaId, imagen_url: url })
    .select()
    .single();
}

/**
 * @param {string} bannerId
 * @param {object} cambios
 */
export async function actualizarBanner(bannerId, cambios) {
  return supabaseClient().from('banner').update(cambios).eq('id', bannerId).select().single();
}

/**
 * Reemplaza la imagen de un banner: sube la nueva a Storage, actualiza la
 * fila y borra el archivo viejo (best-effort). No deja huérfanos en el
 * bucket a diferencia de un update directo de `imagen_url`.
 *
 * @param {string} bannerId
 * @param {File} archivo
 * @returns {Promise<{ data: object | null, error: Error | null }>}
 */
export async function reemplazarImagenBanner(bannerId, archivo) {
  const cliente = supabaseClient();
  const empresaId = empresaIdActual();

  const { data: banner, error: errorLectura } = await cliente
    .from('banner')
    .select('imagen_url')
    .eq('id', bannerId)
    .single();
  if (errorLectura) return { data: null, error: errorLectura };

  const { url, error: errorSubida } = await subirImagenBanner(empresaId, archivo);
  if (errorSubida) return { data: null, error: errorSubida };

  const { data: actualizado, error: errorActualizar } = await cliente
    .from('banner')
    .update({ imagen_url: url })
    .eq('id', bannerId)
    .select()
    .single();
  if (errorActualizar) return { data: null, error: errorActualizar };

  const rutaVieja = rutaDesdeUrlPublica(banner.imagen_url);
  if (rutaVieja) await eliminarArchivo(rutaVieja);

  return { data: actualizado, error: null };
}

/**
 * Persiste un nuevo orden de los banners (el índice en `idsEnOrden` se
 * escribe como `orden` en cada fila). Se usa al reordenar las tarjetas del
 * editor. No toca nada más del banner.
 *
 * @param {string[]} idsEnOrden
 * @returns {Promise<{ error: Error | null }>}
 */
export async function reordenarBanners(idsEnOrden) {
  const cliente = supabaseClient();
  for (let i = 0; i < idsEnOrden.length; i += 1) {
    const { error } = await cliente.from('banner').update({ orden: i }).eq('id', idsEnOrden[i]);
    if (error) return { error };
  }
  return { error: null };
}

/**
 * @param {string} bannerId
 *
 * Antes de borrar la fila, quita el id de cualquier `bloque_pagina.
 * configuracion.ids_banner` que lo referencie — no hay forma de expresar
 * esa integridad referencial con una FK normal porque el id vive dentro de
 * un array en una columna jsonb. Sin este paso, el bloque "Banner
 * principal" quedaría apuntando a un banner que ya no existe.
 */
export async function eliminarBanner(bannerId) {
  const cliente = supabaseClient();

  const { data: banner, error: errorLectura } = await cliente
    .from('banner')
    .select('imagen_url')
    .eq('id', bannerId)
    .single();
  if (errorLectura) return { error: errorLectura };

  const { data: bloquesConReferencia, error: errorBloques } = await cliente
    .from('bloque_pagina')
    .select('id, configuracion')
    .contains('configuracion', { ids_banner: [bannerId] });
  if (errorBloques) return { error: errorBloques };

  for (const bloque of bloquesConReferencia ?? []) {
    const idsSinBanner = (bloque.configuracion?.ids_banner ?? []).filter((id) => id !== bannerId);
    const { error: errorLimpieza } = await cliente
      .from('bloque_pagina')
      .update({ configuracion: { ...bloque.configuracion, ids_banner: idsSinBanner } })
      .eq('id', bloque.id);
    if (errorLimpieza) return { error: errorLimpieza };
  }

  const { error: errorBorrado } = await cliente.from('banner').delete().eq('id', bannerId);
  if (errorBorrado) return { error: errorBorrado };

  const ruta = rutaDesdeUrlPublica(banner.imagen_url);
  if (ruta) await eliminarArchivo(ruta);

  return { error: null };
}
